import type { ISerialize } from '@/objects/ISerialize';
import type { ProductionColumnType } from './../enums/ProductionColumnType';
import type { ProductionTableType } from './../enums/ProductionTableType';
import type { ProductionResultType } from './../enums/ProductionResultType';

export class ProductionTableDef implements ISerialize {
    public AvailableColumns: ProductionColumnType[];

    public Categories: string[];

    public CategoryId: number;

    public Columns: ProductionColumnType[];

    public DisplayType: ProductionTableType;

    public EventId: number;

    public ItemCount: number;

    public Lap: number;

    public OverrideSubtitle: string | null;

    public OverrideTitle: string | null;

    public RaceId: number;

    public ResultType: ProductionResultType | null;

    public RotateTimeInSeconds: number = 120;

    public StartFrom: number;

    public StationRoleId: number;

    public StationRoles: string[];

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        AvailableColumns?: ProductionColumnType[];
        Categories?: string[];
        CategoryId?: number;
        Columns?: ProductionColumnType[];
        DisplayType?: ProductionTableType;
        EventId?: number;
        ItemCount?: number;
        Lap?: number;
        OverrideSubtitle?: string | null;
        OverrideTitle?: string | null;
        RaceId?: number;
        ResultType?: ProductionResultType | null;
        RotateTimeInSeconds?: number;
        StartFrom?: number;
        StationRoleId?: number;
        StationRoles?: string[];
    }) {
        this.AvailableColumns = values.AvailableColumns ?? [];

        this.Categories = values.Categories ?? [];

        this.CategoryId = values.CategoryId ?? 0;

        this.Columns = values.Columns ?? [];

        this.DisplayType = values.DisplayType ?? 'RESULTLIST';

        this.EventId = values.EventId ?? 0;

        this.ItemCount = values.ItemCount ?? 0;

        this.Lap = values.Lap ?? 0;

        this.OverrideSubtitle = values.OverrideSubtitle ?? null;

        this.OverrideTitle = values.OverrideTitle ?? null;

        this.RaceId = values.RaceId ?? 0;

        this.ResultType = values.ResultType ?? 'TOTAL';

        this.RotateTimeInSeconds = values.RotateTimeInSeconds ?? 120;

        this.StartFrom = values.StartFrom ?? 0;

        this.StationRoleId = values.StationRoleId ?? 0;

        this.StationRoles = values.StationRoles ?? [];
    }

    public serialize(): string {
        return (
            '{' +
            `"AvailableColumns": [ ${this.AvailableColumns.map((x) => (x == 'BIB' ? 0 : x == 'NAME' ? 1 : x == 'TIME' ? 2 : x == 'TEAMCLUB' ? 3 : x == 'DIFF' ? 4 : x == 'RANK' ? 5 : x == 'POINTS' ? 6 : x == 'METERS' ? 7 : x == 'LAP' ? 8 : x == 'KM' ? 9 : x == 'STARTTIME' ? 10 : x == 'ROLENAME' ? 11 : x == 'CATEGORY' ? 12 : 0))} ]` +
            ',' +
            `"Categories": [ ${this.Categories.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"CategoryId": ${JSON.stringify(this.CategoryId)}` +
            ',' +
            `"Columns": [ ${this.Columns.map((x) => (x == 'BIB' ? 0 : x == 'NAME' ? 1 : x == 'TIME' ? 2 : x == 'TEAMCLUB' ? 3 : x == 'DIFF' ? 4 : x == 'RANK' ? 5 : x == 'POINTS' ? 6 : x == 'METERS' ? 7 : x == 'LAP' ? 8 : x == 'KM' ? 9 : x == 'STARTTIME' ? 10 : x == 'ROLENAME' ? 11 : x == 'CATEGORY' ? 12 : 0))} ]` +
            ',' +
            `"DisplayType": ${this.DisplayType == 'RESULTLIST' ? 0 : this.DisplayType == 'STARTINGLIST' ? 1 : this.DisplayType == 'SCROLL' ? 2 : 0}` +
            ',' +
            `"EventId": ${JSON.stringify(this.EventId)}` +
            ',' +
            `"ItemCount": ${JSON.stringify(this.ItemCount)}` +
            ',' +
            `"Lap": ${JSON.stringify(this.Lap)}` +
            ',' +
            `"OverrideSubtitle": ${JSON.stringify(this.OverrideSubtitle)}` +
            ',' +
            `"OverrideTitle": ${JSON.stringify(this.OverrideTitle)}` +
            ',' +
            `"RaceId": ${JSON.stringify(this.RaceId)}` +
            ',' +
            `"ResultType": ${this.ResultType == null ? null : this.ResultType == 'TOTAL' ? 0 : this.ResultType == 'GENDER' ? 1 : this.ResultType == 'CATEGORY' ? 2 : null}` +
            ',' +
            `"RotateTimeInSeconds": ${JSON.stringify(this.RotateTimeInSeconds)}` +
            ',' +
            `"StartFrom": ${JSON.stringify(this.StartFrom)}` +
            ',' +
            `"StationRoleId": ${JSON.stringify(this.StationRoleId)}` +
            ',' +
            `"StationRoles": [ ${this.StationRoles.map((x) => JSON.stringify(x))} ]` +
            '}'
        );
    }

    /**
     * Creates an instance of ProductionTableDef from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        AvailableColumns?: ProductionColumnType[];
        Categories?: string[];
        CategoryId?: number;
        Columns?: ProductionColumnType[];
        DisplayType?: ProductionTableType;
        EventId?: number;
        ItemCount?: number;
        Lap?: number;
        OverrideSubtitle?: string | null;
        OverrideTitle?: string | null;
        RaceId?: number;
        ResultType?: ProductionResultType | null;
        RotateTimeInSeconds?: number;
        StartFrom?: number;
        StationRoleId?: number;
        StationRoles?: string[];
    }): ProductionTableDef {
        return new ProductionTableDef(values);
    }

    public static _FromServerJson(json: any): ProductionTableDef {
        return new ProductionTableDef({
            AvailableColumns: json.AvailableColumns?.map((x: any) =>
                x == 0
                    ? 'BIB'
                    : x == 1
                      ? 'NAME'
                      : x == 2
                        ? 'TIME'
                        : x == 3
                          ? 'TEAMCLUB'
                          : x == 4
                            ? 'DIFF'
                            : x == 5
                              ? 'RANK'
                              : x == 6
                                ? 'POINTS'
                                : x == 7
                                  ? 'METERS'
                                  : x == 8
                                    ? 'LAP'
                                    : x == 9
                                      ? 'KM'
                                      : x == 10
                                        ? 'STARTTIME'
                                        : x == 11
                                          ? 'ROLENAME'
                                          : x == 12
                                            ? 'CATEGORY'
                                            : 'BIB',
            ),
            Categories: json.Categories,
            CategoryId: json.CategoryId,
            Columns: json.Columns?.map((x: any) =>
                x == 0
                    ? 'BIB'
                    : x == 1
                      ? 'NAME'
                      : x == 2
                        ? 'TIME'
                        : x == 3
                          ? 'TEAMCLUB'
                          : x == 4
                            ? 'DIFF'
                            : x == 5
                              ? 'RANK'
                              : x == 6
                                ? 'POINTS'
                                : x == 7
                                  ? 'METERS'
                                  : x == 8
                                    ? 'LAP'
                                    : x == 9
                                      ? 'KM'
                                      : x == 10
                                        ? 'STARTTIME'
                                        : x == 11
                                          ? 'ROLENAME'
                                          : x == 12
                                            ? 'CATEGORY'
                                            : 'BIB',
            ),
            DisplayType:
                json.DisplayType == 0
                    ? 'RESULTLIST'
                    : json.DisplayType == 1
                      ? 'STARTINGLIST'
                      : json.DisplayType == 2
                        ? 'SCROLL'
                        : 'RESULTLIST',
            EventId: json.EventId,
            ItemCount: json.ItemCount,
            Lap: json.Lap,
            OverrideSubtitle: json.OverrideSubtitle ?? '',
            OverrideTitle: json.OverrideTitle ?? '',
            RaceId: json.RaceId,
            ResultType:
                json.ResultType == null
                    ? null
                    : json.ResultType == 0
                      ? 'TOTAL'
                      : json.ResultType == 1
                        ? 'GENDER'
                        : json.ResultType == 2
                          ? 'CATEGORY'
                          : null,
            RotateTimeInSeconds: json.RotateTimeInSeconds,
            StartFrom: json.StartFrom,
            StationRoleId: json.StationRoleId,
            StationRoles: json.StationRoles,
        });
    }

    /**
     * Creates an instance of ProductionTableDef from another instance of ProductionTableDef.
     * @param other The ProductionTableDef you want to clone
     * @returns A cloned ProductionTableDef
     */
    public static FromOther(other: ProductionTableDef): ProductionTableDef {
        return ProductionTableDef._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ProductionTableDef.FromOther(this);
    }

    static NAME_OF_CLASS = 'ProductionTableDef';
}
